import { useEffect, useMemo, useState } from 'react';
import {
  useNonSuperAdmins,
  useUpdateUserPermissions,
} from '@/hooks/use-admin-permissions';
import type {
  AdminRole,
  AdminSection,
  AdminUserWithPermissionsRow,
  SectionPermissionItem,
} from '@shared/types/api';

const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'OH / Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Controlling Executive',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Customer Support Executive',
  FINANCE_EXECUTIVE: 'Finance Executive',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance Reviewer',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

interface SectionMeta {
  section: AdminSection;
  label: string;
  description: string;
}

const SECTIONS: SectionMeta[] = [
  {
    section: 'TRANSACTIONS',
    label: 'Transactions',
    description: 'Manage service transactions, stages, and associate assignments.',
  },
  {
    section: 'KYC',
    label: 'KYC',
    description: 'Review associate document submissions, verify or reject KYC.',
  },
  {
    section: 'USERS',
    label: 'Users',
    description: 'View client and associate accounts, suspend or activate accounts.',
  },
  {
    section: 'INVOICES',
    label: 'Invoices',
    description: 'Review generated invoices, payment status, and tax amounts.',
  },
  {
    section: 'PAYOUTS',
    label: 'Payouts',
    description: 'Manage associate payouts, disbursements, and batch payments.',
  },
  {
    section: 'REFUNDS',
    label: 'Refunds',
    description: 'Process and review client cancellation and refund requests.',
  },
  {
    section: 'AUTHORITIES',
    label: 'Authority',
    description: 'Configure and update government and local authorities directory.',
  },
  {
    section: 'SERVICES',
    label: 'Services',
    description: 'Manage service packages, pricing, required documents, and categories.',
  },
  {
    section: 'CONFIG',
    label: 'Config',
    description: 'System configuration, operational toggles, and global policies.',
  },
  {
    section: 'REPORTS',
    label: 'Reports',
    description: 'Financial summaries, transaction turnaround time, and operational reports.',
  },
  {
    section: 'FEEDBACKS',
    label: 'Feedbacks',
    description: 'Review client feedback ratings and associate service reviews.',
  },
  {
    section: 'AUDIT_LOGS',
    label: 'Audit Log',
    description: 'Inspect system-wide audit trails, administrative mutations, and security logs.',
  },
];

export function AdminPermissionsPage() {
  const { data: admins = [], isLoading, isError, refetch } = useNonSuperAdmins();
  const updateMutation = useUpdateUserPermissions();

  const [selectedUser, setSelectedUser] = useState<AdminUserWithPermissionsRow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionsState, setPermissionsState] = useState<Record<AdminSection, SectionPermissionItem>>({} as any);

  // Sync selected user when admins list loads or changes
  useEffect(() => {
    if (admins.length > 0 && !selectedUser) {
      setSelectedUser(admins[0]);
    } else if (selectedUser) {
      const refreshed = admins.find((a) => a.id === selectedUser.id);
      if (refreshed) {
        setSelectedUser(refreshed);
      }
    }
  }, [admins]);

  // When selected user changes, initialize permissions state map
  useEffect(() => {
    if (!selectedUser) return;
    const map: Record<AdminSection, SectionPermissionItem> = {} as any;
    for (const s of SECTIONS) {
      const existing = selectedUser.permissions.find((p) => p.section === s.section);
      map[s.section] = {
        section: s.section,
        canView: existing?.canView ?? false,
        canCreate: existing?.canCreate ?? false,
        canEdit: existing?.canEdit ?? false,
        canDelete: existing?.canDelete ?? false,
      };
    }
    setPermissionsState(map);
  }, [selectedUser]);

  // Track whether any permission checkbox or section toggle differs from saved state
  const isDirty = useMemo(() => {
    if (!selectedUser || !selectedUser.permissions) return false;
    for (const s of SECTIONS) {
      const current = permissionsState[s.section];
      if (!current) return false;
      const saved = selectedUser.permissions.find((p) => p.section === s.section);
      const savedView = Boolean(saved?.canView);
      const savedCreate = Boolean(saved?.canCreate);
      const savedEdit = Boolean(saved?.canEdit);
      const savedDelete = Boolean(saved?.canDelete);

      if (
        Boolean(current.canView) !== savedView ||
        Boolean(current.canCreate) !== savedCreate ||
        Boolean(current.canEdit) !== savedEdit ||
        Boolean(current.canDelete) !== savedDelete
      ) {
        return true;
      }
    }
    return false;
  }, [permissionsState, selectedUser]);

  const filteredAdmins = admins.filter((a) => {
    const query = searchTerm.toLowerCase();
    const fullName = `${a.firstName ?? ''} ${a.lastName ?? ''}`.toLowerCase();
    const email = a.email.toLowerCase();
    const role = a.adminRole ? ADMIN_ROLE_LABELS[a.adminRole].toLowerCase() : '';
    return fullName.includes(query) || email.includes(query) || role.includes(query);
  });

  const handleToggleView = (section: AdminSection, checked: boolean) => {
    setPermissionsState((prev) => {
      const current = prev[section];
      if (!checked) {
        // If view is disabled, disable create, edit, delete as well
        return {
          ...prev,
          [section]: {
            ...current,
            canView: false,
            canCreate: false,
            canEdit: false,
            canDelete: false,
          },
        };
      }
      return {
        ...prev,
        [section]: {
          ...current,
          canView: true,
        },
      };
    });
  };

  const handleToggleAction = (
    section: AdminSection,
    action: 'canCreate' | 'canEdit' | 'canDelete',
    checked: boolean,
  ) => {
    setPermissionsState((prev) => {
      const current = prev[section];
      return {
        ...prev,
        [section]: {
          ...current,
          [action]: checked,
          // If any action is checked, view must automatically be enabled
          canView: checked ? true : current.canView,
        },
      };
    });
  };

  const handleToggleSectionAll = (section: AdminSection, enabled: boolean) => {
    setPermissionsState((prev) => ({
      ...prev,
      [section]: {
        section,
        canView: enabled,
        canCreate: enabled,
        canEdit: enabled,
        canDelete: enabled,
      },
    }));
  };

  const handlePresetAll = (preset: 'FULL' | 'VIEW_ONLY' | 'CLEAR') => {
    setPermissionsState((prev) => {
      const next = { ...prev };
      for (const s of SECTIONS) {
        if (preset === 'FULL') {
          next[s.section] = {
            section: s.section,
            canView: true,
            canCreate: true,
            canEdit: true,
            canDelete: true,
          };
        } else if (preset === 'VIEW_ONLY') {
          next[s.section] = {
            section: s.section,
            canView: true,
            canCreate: false,
            canEdit: false,
            canDelete: false,
          };
        } else {
          next[s.section] = {
            section: s.section,
            canView: false,
            canCreate: false,
            canEdit: false,
            canDelete: false,
          };
        }
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!selectedUser || !isDirty || updateMutation.isPending) return;
    const permissionsArray = Object.values(permissionsState);
    updateMutation.mutate({
      userId: selectedUser.id,
      permissions: permissionsArray,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50/60 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Admin Role & Section Permissions</h1>
          <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
            SUPER ADMIN ONLY
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Select an active admin account to configure granular access permissions (View, Create, Edit, Delete) across system sections.
        </p>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="h-5 w-5 animate-spin text-brand-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Loading admin accounts…
          </div>
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
          Failed to load admin accounts.{' '}
          <button onClick={() => refetch()} className="font-semibold underline">
            Try again
          </button>
        </div>
      ) : admins.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900">No active staff admin accounts</h3>
          <p className="mt-1 text-sm text-gray-500">
            When new admin staff register and are approved in Admin Verification, they will appear here for permission assignments.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Left Panel: Staff Directory */}
          <div className="lg:col-span-4 xl:col-span-4 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white shadow-xs overflow-hidden">
              <div className="border-b border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-900">
                    Staff Accounts ({admins.length})
                  </h2>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search name, email, role…"
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-1.5 text-xs text-gray-900 focus:border-brand-500 focus:bg-white focus:outline-hidden focus:ring-1 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div className="divide-y divide-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
                {filteredAdmins.length === 0 ? (
                  <div className="p-6 text-center text-xs text-gray-400">No matching staff accounts found</div>
                ) : (
                  filteredAdmins.map((admin) => {
                    const isSelected = selectedUser?.id === admin.id;
                    const fullName = [admin.firstName, admin.lastName].filter(Boolean).join(' ') || 'Admin Staff';
                    const initial = fullName.charAt(0).toUpperCase();
                    const activeCount = admin.permissions.filter((p) => p.canView).length;

                    return (
                      <button
                        key={admin.id}
                        type="button"
                        onClick={() => setSelectedUser(admin)}
                        className={`w-full text-left p-4 transition-colors flex items-start gap-3 ${
                          isSelected
                            ? 'bg-brand-50/60 border-l-4 border-l-brand-600'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold text-sm ${
                            isSelected
                              ? 'bg-brand-600 text-white'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-semibold text-sm text-gray-900 truncate">
                              {fullName}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{admin.email}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
                              {admin.adminRole ? (ADMIN_ROLE_LABELS[admin.adminRole] ?? admin.adminRole) : 'Admin'}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                                activeCount > 0
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {activeCount > 0 ? `${activeCount} / 11 Sections Active` : 'No Sections Enabled'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Panel: Permissions Matrix */}
          <div className="lg:col-span-8 xl:col-span-8">
            {selectedUser ? (
              <div className="rounded-xl border border-gray-200 bg-white shadow-xs">
                {/* Active Admin Details Banner */}
                <div className="border-b border-gray-200 p-5 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">
                          {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || 'Admin Staff'}
                        </h2>
                        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                          {selectedUser.adminRole ? (ADMIN_ROLE_LABELS[selectedUser.adminRole] ?? selectedUser.adminRole) : 'Admin'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedUser.email} &bull; Mobile: {selectedUser.mobile || '—'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!isDirty || updateMutation.isPending}
                        title={!isDirty ? 'No changes to save' : 'Save permissions'}
                        className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-brand-800 focus:outline-hidden disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand-700 transition-colors"
                      >
                        {updateMutation.isPending ? (
                          <>
                            <svg className="mr-1.5 h-3.5 w-3.5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Saving…
                          </>
                        ) : (
                          'Save Permissions'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Quick Presets Bar */}
                  <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                    <span className="text-xs font-medium text-gray-500 mr-1">Quick Presets:</span>
                    <button
                      type="button"
                      onClick={() => handlePresetAll('FULL')}
                      className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                    >
                      Full Access (All)
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetAll('VIEW_ONLY')}
                      className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                    >
                      View Only (All)
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetAll('CLEAR')}
                      className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 hover:border-red-200"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Permissions Matrix List */}
                <div className="divide-y divide-gray-100">
                  {SECTIONS.map((sec) => {
                    const state = permissionsState[sec.section] ?? {
                      section: sec.section,
                      canView: false,
                      canCreate: false,
                      canEdit: false,
                      canDelete: false,
                    };
                    const isSectionActive = state.canView || state.canCreate || state.canEdit || state.canDelete;

                    return (
                      <div
                        key={sec.section}
                        className={`p-4 transition-colors ${
                          isSectionActive ? 'bg-white' : 'bg-gray-50/40 opacity-80'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-gray-900">{sec.label}</h3>
                              {isSectionActive ? (
                                <span className="inline-flex items-center rounded-sm bg-emerald-50 px-1.5 py-0.2 text-[10px] font-semibold text-emerald-700">
                                  Enabled
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-sm bg-gray-100 px-1.5 py-0.2 text-[10px] font-semibold text-gray-500">
                                  Disabled
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{sec.description}</p>
                          </div>

                          {/* Quick Section Toggle */}
                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                            <button
                              type="button"
                              onClick={() => handleToggleSectionAll(sec.section, !isSectionActive)}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-md border ${
                                isSectionActive
                                  ? 'border-gray-200 text-gray-600 hover:bg-gray-100'
                                  : 'border-brand-300 text-brand-700 bg-brand-50/50 hover:bg-brand-50'
                              }`}
                            >
                              {isSectionActive ? 'Disable Section' : 'Enable Section'}
                            </button>
                          </div>
                        </div>

                        {/* Granular Checkboxes Bar */}
                        <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-4 gap-2.5 rounded-lg border border-gray-100 bg-gray-50/70 p-2.5">
                          {/* View */}
                          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={state.canView}
                              onChange={(e) => handleToggleView(sec.section, e.target.checked)}
                              className="h-4 w-4 rounded-sm border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span>View</span>
                          </label>

                          {/* Create */}
                          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={state.canCreate}
                              onChange={(e) => handleToggleAction(sec.section, 'canCreate', e.target.checked)}
                              className="h-4 w-4 rounded-sm border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span>Create</span>
                          </label>

                          {/* Edit */}
                          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={state.canEdit}
                              onChange={(e) => handleToggleAction(sec.section, 'canEdit', e.target.checked)}
                              className="h-4 w-4 rounded-sm border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span>Edit</span>
                          </label>

                          {/* Delete */}
                          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={state.canDelete}
                              onChange={(e) => handleToggleAction(sec.section, 'canDelete', e.target.checked)}
                              className="h-4 w-4 rounded-sm border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span>Delete</span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer Save Button */}
                <div className="border-t border-gray-200 p-4 bg-gray-50 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Changes take effect immediately for the user upon saving.
                  </p>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!isDirty || updateMutation.isPending}
                    title={!isDirty ? 'No changes to save' : 'Save permissions'}
                    className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-brand-800 focus:outline-hidden disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand-700 transition-colors"
                  >
                    {updateMutation.isPending ? (
                      <>
                        <svg className="mr-1.5 h-3.5 w-3.5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      'Save Permissions'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex h-96 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
                Select an admin account from the directory to configure their permissions.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
